import moment from 'moment';
import { forceUtc } from '../util/cardUtils';
import { MddFromData, Serie } from './config';
import i18next from 'i18next';
import { NumberFormatType } from 'app/domain/models/enums/NumberFormatType';
import { UserPreferences } from 'app/domain/models/account/UserPreferences';
import { colors } from '@mdd/elements/mdd-styles/colors';
import { TrendIndicatorConfig } from '@mdd/elements/util/DashboardCardIndicatorHandler';
import { getDateTimeLabelFormatsForTooltips } from '../util/getDateTimeLabelFormatsForTooltips';
import { getConstructDateTimeLabel } from '../util/getConstructDateTimeLabels';

enum NumberFormatTypeLocales {
    US = 'en-US',
    EU = 'de-DE'
}
export class UserSettings {
    static key = 'userNumberFormatType';

    static createFromUserPreferences(userPreferences: UserPreferences | undefined) {
        if (userPreferences) {
            // TODO - add other preferences which will need to be accessibl
            UserSettings.numberFormatType = userPreferences.numberFormatting;
        }
    }

    static get storage() {
        return localStorage;
    }
    static set numberFormatType(numberFormatType: NumberFormatType | undefined) {
        const value =
            numberFormatType ||
            NumberFormatType.items.find(
                (item) => item.display === NumberFormatType.DECIMAL_POINT_DOT
            )!.value;
        this.storage.setItem(`mdd.${UserSettings.key}`, value);
    }
    static get numberFormatType() {
        const savedValue = this.storage.getItem(`mdd.${UserSettings.key}`) || '';
        const defaultValue = NumberFormatType.items.find(
            (item) => item.display === NumberFormatType.DECIMAL_POINT_DOT
        )!.value;
        return NumberFormatType[savedValue] || defaultValue;
    }
}

export const getNumberFormatTypeLocale = () => {
    switch (UserSettings.numberFormatType) {
        //case NumberFormatType.items.find(item => item.display === NumberFormatType.DECIMAL_POINT_COMMA)!.value:
        case NumberFormatType.DECIMAL_POINT_COMMA:
            return NumberFormatTypeLocales.EU;
        //case NumberFormatType.items.find(item => item.display === NumberFormatType.DECIMAL_POINT_DOT)!.value:
        case NumberFormatType.DECIMAL_POINT_DOT:
        default:
            return NumberFormatTypeLocales.US;
    }
};

export const fixedDecimalsFormat = (x, decimals: number) => {
    return new Intl.NumberFormat(getNumberFormatTypeLocale(), {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(x);
};

export const getHighchartsLangNumberFormat = () => {
    const formatType = getNumberFormatTypeLocale();
    if (formatType === NumberFormatTypeLocales.US) return { decimalPoint: '.', thousandsSep: ',' };
    if (formatType === NumberFormatTypeLocales.EU) return { decimalPoint: ',', thousandsSep: '.' };
};

export const roundFormat = (x) => {
    if (!x) return x;
    return new Intl.NumberFormat(getNumberFormatTypeLocale()).format(Math.round(Number(x)));
};

export const formatNumber = (x) => {
    return new Intl.NumberFormat(getNumberFormatTypeLocale()).format(x);
};

export const dateTimeFormatUS = (x: string) => {
    if (x === null || x === undefined || x === '') return '';
    const date = moment(forceUtc(x)).utc().format('MM/DD/YY hh:mm A');
    if (date === 'Invalid date') {
        if (typeof x !== 'string') return '';
        let replacedX = x.replace(/-/g, '/');
        const items = replacedX.split('.');
        const finalValue = items[0];
        return moment(forceUtc(finalValue)).utc().format('MM/DD/YY hh:mm A');
    } else {
        return date;
    }
};

export const dateFormatUS = (x: string) => {
    if (x === null || x === undefined || x === '') return '';
    const date = moment(forceUtc(x)).utc().format('MM/DD/YY');
    if (date === 'Invalid date') {
        if (typeof x !== 'string') return '';
        const replacedX = x.replace(/-/g, '/');
        return moment(forceUtc(replacedX)).utc().format('MM/DD/YY');
    } else {
        return date;
    }
};

export const timeFormatUS = (x: string) => {
    return moment(x).format('hh:mm a');
};

export const dateFormatMonthYear = (x: string) => {
    const month = moment(forceUtc(x)).utc().format('MMMM').toLowerCase();
    return (
        `${i18next.t(`date-context:months.long-name.${month}`)}` +
        moment(forceUtc(x)).utc().format(' YYYY')
    );
};

export const dateFormatMonth = (x: string) => {
    const month = moment(forceUtc(x)).utc().format('MMMM').toLowerCase();
    return i18next.t(`date-context:months.long-name.${month}`);
};

export const getFormatter = (serie: Serie) => {
    if (serie.mdd.format) {
        return serie.mdd.format;
    }

    const fraction = serie.mdd.fraction;
    const currency = serie.mdd.currency;

    if (fraction !== undefined || currency) {
        return new Intl.NumberFormat(getNumberFormatTypeLocale(), {
            style: currency ? 'currency' : undefined,
            currency,
            minimumFractionDigits: fraction,
            maximumFractionDigits: fraction
        }).format;
    }

    return (x) => fixedDecimalsFormat(x, 2);
};

export const getMonthNameFromNumberValue = (val: number) => {
    if (val === 1) return 'january';
    else if (val === 2) return 'february';
    else if (val === 3) return 'march';
    else if (val === 4) return 'april';
    else if (val === 5) return 'may';
    else if (val === 6) return 'june';
    else if (val === 7) return 'july';
    else if (val === 8) return 'august';
    else if (val === 9) return 'september';
    else if (val === 10) return 'october';
    else if (val === 11) return 'november';
    else if (val === 12) return 'december';
    else return val;
};

export const getShortMonthNameFromNumberValue = (val: number) => {
    if (val === 1) return 'JAN';
    else if (val === 2) return 'FEB';
    else if (val === 3) return 'MAR';
    else if (val === 4) return 'APR';
    else if (val === 5) return 'MAY';
    else if (val === 6) return 'JUN';
    else if (val === 7) return 'JUL';
    else if (val === 8) return 'AUG';
    else if (val === 9) return 'SEP';
    else if (val === 10) return 'OCT';
    else if (val === 11) return 'NOV';
    else if (val === 12) return 'DEC';
    else return val;
};

export const convertSecondsToHhMm = (seconds: number) => {
    const isNegative = seconds < 0;
    let d = Number(Math.abs(seconds));
    let h = Math.floor(d / 3600);
    let hh = h.toString();
    hh = hh.length == 1 ? '0' + hh : hh;
    let m = Math.floor((d % 3600) / 60);
    let mm = m.toString();
    mm = Math.abs(+mm).toString();
    mm = mm.length == 1 ? '0' + mm : mm;
    return isNegative ? `-${hh}:${mm}` : `${hh}:${mm}`;
};

export const convertSecondsToHhMmSs = (seconds: number) => {
    const isNegative = seconds < 0;
    let d = Math.abs(seconds);

    let h = Math.floor(d / 3600) % 24;
    let hh = h < 10 ? '0' + h : h.toString();

    let m = Math.floor((d % 3600) / 60);
    let mm = m < 10 ? '0' + m : m.toString();

    let s = Math.floor(d % 60);
    let ss = s < 10 ? '0' + s : s.toString();

    return isNegative ? `-${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
};

export const convertSecondsToAmPm = (seconds: number) => {
    let totalHours = Math.floor(seconds / 3600) % 24;
    let minutes = Math.floor((seconds % 3600) / 60);

    const period = totalHours >= 12 ? 'PM' : 'AM';
    const hours = (totalHours % 12 || 12).toString();
    const mm = minutes.toString().padStart(2, '0');

    return `${hours}:${mm} ${period}`;
};

export const convertSecondsToHoursAndMinutes = (seconds: number) => {
    const isNegative = seconds < 0;
    const absSeconds = Math.abs(seconds);

    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);

    const hoursText = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
    const minutesText = minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : '';

    const result = [hoursText, minutesText].filter(Boolean).join(' ');

    return isNegative ? `-${result}` : result || '0 hours';
};

export type TrendIndicatorOptions = {
    suffixValue?: '%';
    invertTrendColor?: boolean;
};

export const getTrendIndicator = (
    value: string | number | null,
    options?: TrendIndicatorOptions
) => {
    if (value) {
        const trendValue = Number(value);
        let trendIndicator: 'up' | 'down' | 'equal' = undefined!;
        if (+trendValue > 0) {
            trendIndicator = 'up';
        } else if (+trendValue < 0) {
            trendIndicator = 'down';
        } else if (+trendValue === 0) {
            trendIndicator = 'equal';
        }

        let color: string = '';
        let backgroundColor: string = '';

        if (trendIndicator === 'up') {
            color = options?.invertTrendColor
                ? colors.indicatorTrendRed
                : colors.indicatorTrendGreen;
            backgroundColor = options?.invertTrendColor
                ? colors.indicatorTrendRedBackground
                : colors.indicatorTrendGreenBackground;
        } else if (trendIndicator === 'down') {
            color = options?.invertTrendColor
                ? colors.indicatorTrendGreen
                : colors.indicatorTrendRed;
            backgroundColor = options?.invertTrendColor
                ? colors.indicatorTrendGreenBackground
                : colors.indicatorTrendRedBackground;
        } else if (trendIndicator === 'equal') {
            color = colors.indicatorTrendGray;
            backgroundColor = colors.indicatorTrendGrayBackground;
        }

        const config: TrendIndicatorConfig = {
            color: color,
            backgroundColor: backgroundColor,
            value: formatNumber(Math.abs(+trendValue)) + (options?.suffixValue ?? ''),
            trend: trendIndicator
        };
        return config;
    }
};

export const getCustomIndicator = (value: string | number | null, trend: 'apex', color: string) => {
    if (value) {
        const numberValue = Number(value);
        const config: TrendIndicatorConfig = {
            color: color,
            backgroundColor: '',
            value: formatNumber(Math.abs(numberValue)),
            trend: trend
        };
        return config;
    }
};

export function getStandardDateForTooltips(
    mddFromData: MddFromData | undefined,
    timestampValue: number
) {
    if (mddFromData) {
        const { isAllDataFromCurrentYear, dataLevel } = mddFromData;
        const { dayStr, monthStr, yearStr } = getDateTimeLabelFormatsForTooltips(
            isAllDataFromCurrentYear,
            true,
            dataLevel
        );
        const date = getConstructDateTimeLabel(dayStr, monthStr, yearStr, timestampValue);
        return date;
    } else {
        const { dayStr, monthStr, yearStr } = getDateTimeLabelFormatsForTooltips(
            false,
            true,
            'days'
        );
        const date = getConstructDateTimeLabel(dayStr, monthStr, yearStr, timestampValue);
        return date;
    }
}

export function getDayFromTimestamp(timestamp: number) {
    const date = moment.utc(timestamp);
    return date.format('dddd');
}

export const areAllDatesFromCurrentYear = (dates: string[]): boolean => {
    if (!dates || !dates.length) return false;
    const currentYear = moment.utc().year();

    // check if all dates are from current year
    for (let i = 0; i < dates.length; i++) {
        const date = dates[i];
        const dateYear = moment(forceUtc(date)).utc().year();
        if (dateYear !== currentYear) return false;
    }

    return true;
};

/**
 * Updates the formatters of the passed series to return the empty value when the value is null
 * @param series - Series to update
 * @param emptyValue - Value to replace null values with
 * @returns Returns a copy of the passed series with the formatters updated to return the empty value when the value is null
 */
export const withEmptyValue = <TSerie extends Serie>(series: TSerie[], emptyValue: string) => {
    return series.map((serie) => {
        const formatter = getFormatter(serie);

        return {
            ...serie,
            ...(formatter && {
                mdd: {
                    ...serie.mdd,
                    format: (value, allColumns) =>
                        value === null ? emptyValue : formatter(value, allColumns)
                }
            })
        };
    });
};

export const withDashEmptyValue = <TSerie extends Serie>(series: TSerie[]) => {
    return withEmptyValue(series, '-');
};

/**
 * Rounds a number to the nearest thousand.
 * @example
 * roundToNearestThousand(1234) -> 1000
 * roundToNearestThousand(12345) -> 12000
 * roundToNearestThousand(500) -> 0
 * @param x - Number to round
 * @returns Rounded number
 */
export const roundToNearestThousand = (x: number): number => {
    return Math.round(x / 1000) * 1000;
};
